<template>
  <div class="file-manager-container">
    <!-- Header Selection Area -->
    <v-sheet class="pa-6 mb-6 rounded-lg border bg-grey-light">
      <div class="d-flex align-center mb-4">
        <v-icon small color="primary" class="mr-2">fas fa-paperclip</v-icon>
        <span class="text-subtitle-2 font-weight-black grey--text text--darken-2 text-uppercase tracking-widest">
          Adjuntar Documentos
        </span>
      </div>
      <file-input @change="addFiles" />
    </v-sheet>

    <!-- Files List Area -->
    <div v-if="cloudFiles.length > 0 || files.length > 0" class="files-list mb-6">
      <div class="px-2 mb-2 d-flex align-center">
        <span class="caption font-weight-black grey--text text-uppercase tracking-wider">Archivos Seleccionados</span>
        <v-divider class="ml-4"></v-divider>
      </div>

      <v-list class="transparent pa-0">
        <!-- Already Uploaded Files -->
        <v-list-item 
          v-for="oldFile in cloudFiles" 
          :key="oldFile.url"
          class="modern-file-item mb-2 rounded-lg border shadow-xs transition-swing"
          :class="{ 'delete-pending': oldFile.delete }"
        >
          <v-list-item-avatar color="blue lighten-5" size="40">
            <v-icon :color="oldFile.delete ? 'error' : 'blue'">{{ getFileIcon(oldFile.name) }}</v-icon>
          </v-list-item-avatar>

          <v-list-item-content>
            <v-list-item-title class="body-2 font-weight-bold" :class="{ 'error--text text-decoration-line-through': oldFile.delete }">
              <a v-if="!oldFile.delete" :href="oldFile.url" target="_blank" class="file-link">{{ oldFile.name }}</a>
              <span v-else>{{ oldFile.name }}</span>
            </v-list-item-title>
            <v-list-item-subtitle class="caption d-flex align-center">
              <v-chip x-small label :color="oldFile.delete ? 'error' : 'success'" class="mr-2 font-weight-bold px-1">
                {{ oldFile.delete ? 'PARA ELIMINAR' : 'EN LA NUBE' }}
              </v-chip>
            </v-list-item-subtitle>
          </v-list-item-content>

          <v-list-item-action>
            <v-btn icon small @click="oldFile.delete = !oldFile.delete" :color="oldFile.delete ? 'info' : 'error'">
              <v-icon small>{{ oldFile.delete ? 'fas fa-undo' : 'fas fa-trash-alt' }}</v-icon>
            </v-btn>
          </v-list-item-action>
        </v-list-item>

        <!-- New Local Files to be Uploaded -->
        <v-list-item 
          v-for="(file, index) in files" 
          :key="'local-' + index"
          class="modern-file-item local-file mb-2 rounded-lg border shadow-xs border-left-success"
        >
          <v-list-item-avatar color="success lighten-5" size="40">
            <v-icon color="success">{{ getFileIcon(file.name) }}</v-icon>
          </v-list-item-avatar>

          <v-list-item-content>
            <v-list-item-title class="body-2 font-weight-bold success--text">{{ file.name }}</v-list-item-title>
            <v-list-item-subtitle class="caption d-flex align-center">
              <v-chip x-small label color="success" class="mr-2 font-weight-bold px-1">LOCAL / NUEVO</v-chip>
              <span v-if="file.size" class="grey--text">{{ (file.size / 1024).toFixed(1) }} KB</span>
            </v-list-item-subtitle>
          </v-list-item-content>

          <v-list-item-action>
            <v-btn icon small @click="removeLocalFile(file)" color="error">
              <v-icon small>fas fa-times-circle</v-icon>
            </v-btn>
          </v-list-item-action>
        </v-list-item>
      </v-list>
    </div>

    <div v-else class="text-center pa-10 grey--text opacity-50 border rounded-lg mb-6 bg-grey-light">
      <v-icon x-large class="mb-3">fas fa-folder-open</v-icon>
      <div class="text-subtitle-2">No hay documentos adjuntos</div>
      <div class="caption">Sube archivos PDF, imágenes o documentos para esta OT</div>
    </div>

    <!-- Action Bar -->
    <v-divider v-if="cloudFiles.length > 0 || files.length > 0" class="mb-4"></v-divider>
    <div class="d-flex justify-end pt-2">
      <v-btn 
        color="primary" 
        block
        large
        elevation="2"
        :disabled="files.length === 0 && !hasDeletions"
        :loading="loading" 
        class="rounded-lg font-weight-black text-uppercase tracking-wider shadow-md" 
        @click="saveFiles"
      >
        <v-icon left>fas fa-sync-alt</v-icon> Sincronizar Documentos
      </v-btn>           
    </div> 
  </div>
</template>

<script>
import FileInput from '@/components/utils/FileInput.vue'
import { updateSalesNote } from '@/api/salesNotes'
import notifications from '@/utils/notifications'

export default {
  name: 'FileManager',
  components: {
    FileInput
  },
  props: ['oldFiles', 'sales_note'],
  data() {
    return {
      files: [],
      cloudFiles: this.oldFiles ? JSON.parse(JSON.stringify(this.oldFiles)) : [],
      loading: false
    }
  },
  computed: {
    hasDeletions() {
      return this.cloudFiles.some(f => f.delete)
    }
  },
  methods: {
    addFiles(files) {
      this.files = [...this.files, ...files]
    },
    removeLocalFile(file) {
      this.files.splice(this.files.indexOf(file), 1)
    },
    getFileIcon(filename) {
      const ext = filename.split('.').pop().toLowerCase()
      const icons = {
        pdf: 'fas fa-file-pdf',
        jpg: 'fas fa-file-image',
        jpeg: 'fas fa-file-image',
        png: 'fas fa-file-image',
        doc: 'fas fa-file-word',
        docx: 'fas fa-file-word',
        xls: 'fas fa-file-excel',
        xlsx: 'fas fa-file-excel',
        csv: 'fas fa-file-csv',
        zip: 'fas fa-file-archive',
        rar: 'fas fa-file-archive'
      }
      return icons[ext] || 'fas fa-file-alt'
    },
    async saveFiles() {
      this.loading = true
      var formData = new FormData()
      this.files.forEach((file, index) => {
        formData.append(`file${index}`, file)
      })
      formData.append('oldFiles', JSON.stringify(this.cloudFiles))
      
      try {
        await updateSalesNote(this.sales_note, formData)
        notifications.showSuccess('Documentos actualizados correctamente')
        this.$emit('refresh')
      } catch (error) {
        notifications.showError(error.response?.data?.message || 'Error al actualizar documentos')
      } finally {
        this.loading = false
      }
    }
  },
  watch: {
    oldFiles: {
      handler(val) {
        this.cloudFiles = val ? JSON.parse(JSON.stringify(val)) : []
        this.files = []
      },
      deep: true,
      immediate: true
    }
  }
}
</script>

<style scoped>
.file-manager-container {
  max-width: 100%;
}
.bg-grey-light {
  background-color: #f8f9fa;
}
.modern-file-item {
  background-color: #fff;
  border: 1px solid rgba(0,0,0,0.08) !important;
  transition: all 0.2s ease;
}
.modern-file-item:hover {
  border-color: var(--v-primary-base) !important;
  background-color: rgba(var(--v-primary-base), 0.01);
}
.local-file {
  border-left: 4px solid var(--v-success-base) !important;
}
.delete-pending {
  background-color: #fff5f5 !important;
  border-color: rgba(var(--v-error-base), 0.2) !important;
}
.file-link {
  color: var(--v-primary-base);
  text-decoration: none;
}
.file-link:hover {
  text-decoration: underline;
}
.border-left-success {
  border-left: 4px solid var(--v-success-base) !important;
}
.tracking-widest {
  letter-spacing: 2px;
}
.tracking-wider {
  letter-spacing: 1px;
}
.opacity-50 {
  opacity: 0.5;
}
.shadow-xs {
  box-shadow: 0 1px 2px rgba(0,0,0,0.05) !important;
}
.shadow-md {
  box-shadow: 0 4px 12px rgba(var(--v-primary-base), 0.2) !important;
}
</style>